import { useEffect, useRef, useState } from "react";
import { Search, MapPin, Loader2, X } from "lucide-react";

// OpenStreetMap Nominatim. free, no key. Please stay courteous (1 req / typing-debounce).
const NOMINATIM = "https://nominatim.openstreetmap.org/search";

export default function AddressSearch({ onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const container = useRef(null);

  useEffect(() => {
    const outside = (e) => {
      if (container.current && !container.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", outside);
    return () => window.removeEventListener("mousedown", outside);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim() || q.trim().length < 3) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `${NOMINATIM}?q=${encodeURIComponent(q)}&format=json&limit=6&addressdetails=1`;
        const res = await fetch(url, {
          headers: { Accept: "application/json" },
        });
        const data = await res.json();
        setResults(data || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 450);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const pick = (r) => {
    const lon = parseFloat(r.lon);
    const lat = parseFloat(r.lat);
    const bb = (r.boundingbox || []).map(parseFloat); // [s, n, w, e]
    const bbox =
      bb.length === 4 && bb.every(isFinite)
        ? [bb[2], bb[0], bb[3], bb[1]]
        : [lon - 0.2, lat - 0.2, lon + 0.2, lat + 0.2];
    onSelect?.({
      lon,
      lat,
      bbox,
      label: r.display_name,
      type: r.type,
    });
    setQ(r.display_name.split(",").slice(0, 2).join(","));
    setOpen(false);
  };

  const clear = () => {
    setQ("");
    setResults([]);
    setOpen(false);
  };

  return (
    <div ref={container} className="relative" data-testid="address-search">
      <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-black/40 px-3 py-2">
        <Search className="h-4 w-4 shrink-0 text-emerald-400" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search a place to center the map (e.g. Silent Valley, Kerala)"
          data-testid="address-search-input"
          className="flex-1 bg-transparent text-sm text-white placeholder-slate-500 outline-none"
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        {q && !loading && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear search"
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-white/10 hover:text-white"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div
          data-testid="address-search-results"
          className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-white/10 bg-arjuna-ink/95 shadow-[0_30px_80px_-30px_rgba(0,229,255,0.3)] backdrop-blur-md"
        >
          <ul className="max-h-72 overflow-y-auto">
            {results.map((r) => (
              <li key={r.place_id}>
                <button
                  type="button"
                  onClick={() => pick(r)}
                  data-testid={`address-result-${r.place_id}`}
                  className="flex w-full items-start gap-3 px-4 py-3 text-left text-sm text-slate-200 transition-colors hover:bg-emerald-500/10 hover:text-emerald-300"
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{r.display_name.split(",").slice(0, 2).join(",")}</p>
                    <p className="truncate text-[11px] text-slate-500">
                      {r.display_name}
                    </p>
                  </div>
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    {r.type || "place"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <p className="border-t border-white/5 bg-black/30 px-3 py-1.5 text-[10px] text-slate-500">
            Powered by © OpenStreetMap contributors
          </p>
        </div>
      )}
    </div>
  );
}
